import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import './DeskChat.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('sessionToken')}` });
const ROOMS_POLL_MS = 20000;
const PAGE = 60;

function parseTs(ts) {
  if (!ts) return null;
  const d = typeof ts === 'number' ? new Date(ts) : new Date(String(ts).includes('T') ? ts : `${String(ts).replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}
function fmtClock(ts) {
  const d = parseTs(ts);
  if (!d) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  return sameDay ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
}
function fmtNum(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  const d = n >= 10000 ? 1 : n >= 1000 ? 2 : n >= 100 ? 3 : n >= 1 ? 4 : 6;
  return n.toLocaleString(undefined, { maximumFractionDigits: d });
}
function dayLabel(ts) {
  const d = parseTs(ts);
  if (!d) return '';
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}
function rr(sig) {
  const e = Number(sig.entry);
  const s = Number(sig.stop);
  const t = Number(sig.target);
  if (![e, s, t].every(Number.isFinite) || e === s) return null;
  const risk = Math.abs(e - s);
  const reward = Math.abs(t - e);
  return risk > 0 ? reward / risk : null;
}

/* ------------------------------- signal card ------------------------------ */

function SignalCard({ signal, compact, isAdmin, onLoad, onClose, mid }) {
  const [closing, setClosing] = useState(false);
  const [status, setStatus] = useState('closed');
  const [price, setPrice] = useState('');
  const [note, setNote] = useState('');
  const active = signal.status === 'active';
  const ratio = rr(signal);

  return (
    <div className={`dsk-signal dsk-signal-${signal.side} ${active ? 'active' : 'done'}${compact ? ' compact' : ''}`}>
      <div className="dsk-signal-head">
        <span className={`dsk-side ${signal.side}`}>{signal.side.toUpperCase()}</span>
        <strong>{signal.symbol}</strong>
        {signal.dex && signal.dex !== 'main' && <small className="dsk-dex">{signal.dex}</small>}
        <span className={`dsk-status ${signal.status}`}>{signal.status}</span>
        <span className="dsk-signal-meta">
          #{signal.id} · {signal.author} · {fmtClock(signal.createdAt)}
        </span>
      </div>
      <div className="dsk-signal-levels">
        <div><span>Entry</span><b>{fmtNum(signal.entry)}</b></div>
        <div><span>Stop</span><b className="neg">{fmtNum(signal.stop)}</b></div>
        <div><span>Target</span><b className="pos">{fmtNum(signal.target)}</b></div>
        {ratio != null && <div><span>R:R</span><b>{ratio.toFixed(1)}</b></div>}
        {!active && signal.closePrice != null && <div><span>Closed @</span><b>{fmtNum(signal.closePrice)}</b></div>}
      </div>
      {signal.note && <div className="dsk-signal-note">{signal.note}</div>}
      {!active && signal.closeNote && <div className="dsk-signal-note muted">{signal.closeNote}</div>}
      {signal.expiresAt && active && <div className="dsk-signal-exp">expires {fmtClock(signal.expiresAt)}</div>}
      <div className="dsk-signal-actions">
        <button type="button" className="dsk-btn primary" onClick={() => onLoad(signal)}>
          Load into ticket
        </button>
        {isAdmin && active && !closing && (
          <button type="button" className="dsk-btn" onClick={() => { setClosing(true); setPrice(mid != null ? String(Math.round(mid * 100) / 100) : ''); }}>
            Close signal
          </button>
        )}
      </div>
      {closing && (
        <div className="dsk-close-form">
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="target">Hit target</option>
            <option value="stopped">Stopped out</option>
            <option value="closed">Closed manually</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <input type="number" step="any" placeholder="price" value={price} onChange={(e) => setPrice(e.target.value)} />
          <input type="text" placeholder="note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className="dsk-btn primary" onClick={() => onClose(signal, { status, price, note }).then(() => setClosing(false))}>
            Confirm
          </button>
          <button type="button" className="dsk-btn" onClick={() => setClosing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- signal form ------------------------------ */

function SignalForm({ market, mid, onSubmit, onCancel }) {
  const [side, setSide] = useState('long');
  const [symbol, setSymbol] = useState(market?.symbol || 'BTC');
  const [dex, setDex] = useState(market?.dex || 'main');
  const [entry, setEntry] = useState(mid != null ? String(Math.round(mid * 100) / 100) : '');
  const [stop, setStop] = useState('');
  const [target, setTarget] = useState('');
  const [note, setNote] = useState('');
  const [expiresMin, setExpiresMin] = useState('120');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSymbol(market?.symbol || 'BTC');
    setDex(market?.dex || 'main');
  }, [market?.symbol, market?.dex]);

  const ratio = rr({ entry, stop, target });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({ side, symbol, dex, coin: market?.symbol === symbol ? market?.coin : undefined, entry, stop, target, note, expiresMin });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="dsk-signal-form" onSubmit={submit}>
      <div className="dsk-sf-row">
        <div className="dsk-side-toggle">
          <button type="button" className={side === 'long' ? 'on long' : ''} onClick={() => setSide('long')}>LONG</button>
          <button type="button" className={side === 'short' ? 'on short' : ''} onClick={() => setSide('short')}>SHORT</button>
        </div>
        <input className="dsk-sym" value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="Symbol" required />
        <input className="dsk-dex-in" value={dex} onChange={(e) => setDex(e.target.value.toLowerCase())} placeholder="dex" title="main or HIP-3 dex (xyz, flx, km…)" />
      </div>
      <div className="dsk-sf-row">
        <label><span>Entry</span><input type="number" step="any" value={entry} onChange={(e) => setEntry(e.target.value)} /></label>
        <label><span>Stop</span><input type="number" step="any" value={stop} onChange={(e) => setStop(e.target.value)} /></label>
        <label><span>Target</span><input type="number" step="any" value={target} onChange={(e) => setTarget(e.target.value)} /></label>
        <label><span>Expires</span>
          <select value={expiresMin} onChange={(e) => setExpiresMin(e.target.value)}>
            <option value="30">30 min</option>
            <option value="60">1 h</option>
            <option value="120">2 h</option>
            <option value="240">4 h</option>
            <option value="720">12 h</option>
            <option value="">never</option>
          </select>
        </label>
      </div>
      <textarea rows={2} placeholder="Reasoning / invalidation (optional)" value={note} onChange={(e) => setNote(e.target.value)} maxLength={600} />
      <div className="dsk-sf-foot">
        <span className="dsk-muted">{ratio != null ? `R:R ${ratio.toFixed(1)}` : 'Add stop + target for R:R'} · posts to Desk as a signal card</span>
        <div>
          <button type="button" className="dsk-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className={`dsk-btn primary ${side}`} disabled={busy || !symbol}>Post {side} signal</button>
        </div>
      </div>
    </form>
  );
}

/* --------------------------------- main ---------------------------------- */

export default function DeskChat({ visible, isAdmin, market, mid, onLoadSignal, onUnread, onToast }) {
  const [me, setMe] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [maxBody, setMaxBody] = useState(2000);
  const [activeId, setActiveId] = useState(null);
  const [view, setView] = useState('room'); // room | signals
  const [msgs, setMsgs] = useState({}); // roomId -> { list, hasMore }
  const [signals, setSignals] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [streamOk, setStreamOk] = useState(false);
  const [showSignalForm, setShowSignalForm] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const listRef = useRef(null);
  const atBottomRef = useRef(true);
  const lastReadSent = useRef({});
  const activeIdRef = useRef(null);
  const visibleRef = useRef(visible);
  const meRef = useRef(null);
  const msgsRef = useRef({});
  activeIdRef.current = activeId;
  visibleRef.current = visible;
  meRef.current = me;
  msgsRef.current = msgs;

  const toast = useCallback((kind, text) => onToast && onToast(kind, text), [onToast]);
  const errText = (e) => e?.response?.data?.error || e?.message || 'Request failed';

  /* ---- rooms ---- */
  const loadRooms = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/disc-scalping/chat/rooms`, { headers: authHeaders() });
      setMe(data.me);
      setRooms(data.rooms || []);
      setContacts(data.contacts || []);
      if (data.maxBody) setMaxBody(data.maxBody);
      setActiveId((cur) => cur ?? data.rooms?.find((r) => r.kind === 'group')?.id ?? null);
    } catch (e) {
      // keep old state; SSE/poll will retry
    }
  }, []);

  useEffect(() => {
    loadRooms();
    const t = setInterval(loadRooms, ROOMS_POLL_MS);
    return () => clearInterval(t);
  }, [loadRooms]);

  useEffect(() => {
    if (onUnread) onUnread(rooms.reduce((a, r) => a + (r.unread || 0), 0));
  }, [rooms, onUnread]);

  /* ---- messages ---- */
  const loadMessages = useCallback(async (roomId, { before, after } = {}) => {
    const params = { limit: PAGE };
    if (before) params.before = before;
    if (after) params.after = after;
    const { data } = await axios.get(`${API_BASE}/disc-scalping/chat/rooms/${roomId}/messages`, { headers: authHeaders(), params });
    setMsgs((prev) => {
      const cur = prev[roomId] || { list: [], hasMore: false };
      let list;
      if (before) list = data.messages.concat(cur.list);
      else if (after) list = cur.list.concat(data.messages);
      else list = data.messages;
      const seen = new Set();
      list = list.filter((m) => (seen.has(m.id) ? false : (seen.add(m.id), true)));
      return { ...prev, [roomId]: { list, hasMore: before || !after ? data.hasMore : cur.hasMore, loaded: true } };
    });
    return data;
  }, []);

  useEffect(() => {
    if (activeId == null) return;
    const cur = msgs[activeId];
    if (!cur?.loaded) {
      loadMessages(activeId).catch((e) => toast('err', errText(e)));
    } else {
      // Back-fill anything missed while the room was not active.
      const lastId = cur.list.length ? cur.list[cur.list.length - 1].id : 0;
      loadMessages(activeId, { after: lastId }).catch(() => {});
    }
    atBottomRef.current = true;
    setShowSignalForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const activeRoom = useMemo(() => rooms.find((r) => r.id === activeId) || null, [rooms, activeId]);
  const activeEntry = msgs[activeId];
  const activeList = useMemo(() => activeEntry?.list || [], [activeEntry]);

  /* ---- mark read when viewing ---- */
  useEffect(() => {
    if (!visible || view !== 'room' || activeId == null || !activeList.length) return;
    const last = activeList[activeList.length - 1].id;
    if ((lastReadSent.current[activeId] || 0) >= last) return;
    lastReadSent.current[activeId] = last;
    axios
      .post(`${API_BASE}/disc-scalping/chat/rooms/${activeId}/read`, { lastId: last }, { headers: authHeaders() })
      .then(() => setRooms((rs) => rs.map((r) => (r.id === activeId ? { ...r, unread: 0 } : r))))
      .catch(() => {});
  }, [visible, view, activeId, activeList]);

  /* ---- auto-scroll ---- */
  useEffect(() => {
    const el = listRef.current;
    if (!el || !visible) return;
    if (atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [activeList, visible, view, activeId]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  /* ---- signals list ---- */
  const loadSignals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/disc-scalping/chat/signals`, { headers: authHeaders(), params: { status: 'all', limit: 60 } });
      setSignals(data.signals || []);
    } catch (e) {
      toast('err', errText(e));
    }
  }, [toast]);
  useEffect(() => {
    if (view === 'signals' && signals == null) loadSignals();
  }, [view, signals, loadSignals]);

  /* ---- live stream ---- */
  useEffect(() => {
    const token = localStorage.getItem('sessionToken') || '';
    const es = new EventSource(`${API_BASE}/disc-scalping/chat/stream?token=${encodeURIComponent(token)}`);
    const parse = (ev) => {
      try {
        return JSON.parse(ev.data);
      } catch {
        return null;
      }
    };
    es.addEventListener('hello', () => {
      setStreamOk(true);
      loadRooms();
      // (Re)connected: back-fill anything the active room missed while offline.
      const id = activeIdRef.current;
      const cur = id != null ? msgsRef.current[id] : null;
      if (cur?.loaded) {
        const lastId = cur.list.length ? cur.list[cur.list.length - 1].id : 0;
        loadMessages(id, { after: lastId }).catch(() => {});
      }
    });
    es.addEventListener('message', (ev) => {
      const p = parse(ev);
      if (!p?.message) return;
      const { roomId, message } = p;
      setMsgs((prev) => {
        const cur = prev[roomId];
        if (!cur?.loaded) return prev;
        if (cur.list.some((m) => m.id === message.id)) return prev;
        return { ...prev, [roomId]: { ...cur, list: cur.list.concat(message) } };
      });
      const mine = meRef.current && message.userId === meRef.current.id;
      const viewing = visibleRef.current && activeIdRef.current === roomId;
      setRooms((rs) => {
        if (!rs.some((r) => r.id === roomId)) {
          loadRooms();
          return rs;
        }
        return rs.map((r) =>
          r.id === roomId
            ? {
                ...r,
                unread: mine || viewing ? r.unread : (r.unread || 0) + 1,
                last: { id: message.id, userId: message.userId, author: message.author, kind: message.kind, preview: message.kind === 'signal' ? 'Signal' : message.body.slice(0, 80), createdAt: message.createdAt },
              }
            : r
        );
      });
      if (message.kind === 'signal' && message.meta?.signal) {
        setSignals((s) => (s ? [message.meta.signal].concat(s.filter((x) => x.id !== message.meta.signal.id)) : s));
        if (!mine) toast('ok', `New signal: ${message.meta.signal.side.toUpperCase()} ${message.meta.signal.symbol}`);
      }
    });
    es.addEventListener('deleted', (ev) => {
      const p = parse(ev);
      if (!p) return;
      setMsgs((prev) => {
        const cur = prev[p.roomId];
        if (!cur) return prev;
        return { ...prev, [p.roomId]: { ...cur, list: cur.list.map((m) => (m.id === p.id ? { ...m, deleted: true, body: '', meta: null } : m)) } };
      });
    });
    es.addEventListener('signal', (ev) => {
      const p = parse(ev);
      if (!p?.signal) return;
      setSignals((s) => (s ? s.map((x) => (x.id === p.signal.id ? p.signal : x)) : s));
      setMsgs((prev) => {
        const isCard = (m) => m.kind === 'signal' && m.meta?.signal?.id === p.signal.id;
        const touched = Object.entries(prev).filter(([, cur]) => cur.list.some(isCard));
        if (!touched.length) return prev;
        const out = { ...prev };
        for (const [rid, cur] of touched) {
          out[rid] = { ...cur, list: cur.list.map((m) => (isCard(m) ? { ...m, meta: { ...m.meta, signal: p.signal } } : m)) };
        }
        return out;
      });
    });
    es.addEventListener('room', () => loadRooms());
    es.onerror = () => setStreamOk(false);
    return () => es.close();
  }, [loadRooms, loadMessages, toast]);

  /* ---- actions ---- */
  const send = async () => {
    const body = draft.trim();
    if (!body || activeId == null || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API_BASE}/disc-scalping/chat/rooms/${activeId}/messages`, { body }, { headers: authHeaders() });
      setDraft('');
      atBottomRef.current = true;
      setMsgs((prev) => {
        const cur = prev[activeId] || { list: [], hasMore: false, loaded: true };
        if (cur.list.some((m) => m.id === data.message.id)) return prev;
        return { ...prev, [activeId]: { ...cur, list: cur.list.concat(data.message) } };
      });
    } catch (e) {
      toast('err', errText(e));
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const openDm = async (userId) => {
    if (!userId) return;
    const existing = contacts.find((c) => c.id === Number(userId))?.roomId;
    if (existing) {
      setView('room');
      setActiveId(existing);
      return;
    }
    try {
      const { data } = await axios.post(`${API_BASE}/disc-scalping/chat/dm`, { userId: Number(userId) }, { headers: authHeaders() });
      await loadRooms();
      setView('room');
      setActiveId(data.room.id);
    } catch (e) {
      toast('err', errText(e));
    }
  };

  const del = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API_BASE}/disc-scalping/chat/messages/${m.id}`, { headers: authHeaders() });
    } catch (e) {
      toast('err', errText(e));
    }
  };

  const postSignal = async (form) => {
    try {
      await axios.post(`${API_BASE}/disc-scalping/chat/signals`, form, { headers: authHeaders() });
      setShowSignalForm(false);
      toast('ok', `Signal posted: ${form.side.toUpperCase()} ${form.symbol}`);
    } catch (e) {
      toast('err', errText(e));
    }
  };

  const closeSignal = async (signal, body) => {
    try {
      await axios.post(`${API_BASE}/disc-scalping/chat/signals/${signal.id}/close`, body, { headers: authHeaders() });
      toast('ok', `Signal #${signal.id} ${body.status}`);
    } catch (e) {
      toast('err', errText(e));
    }
  };

  const loadOlder = async () => {
    const cur = msgs[activeId];
    if (!cur?.list?.length || loadingOlder) return;
    const el = listRef.current;
    const beforeH = el ? el.scrollHeight : 0;
    setLoadingOlder(true);
    try {
      await loadMessages(activeId, { before: cur.list[0].id });
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - beforeH;
      });
    } catch (e) {
      toast('err', errText(e));
    } finally {
      setLoadingOlder(false);
    }
  };

  /* ---- render ---- */
  const dmRooms = rooms.filter((r) => r.kind === 'dm');
  const deskRoom = rooms.find((r) => r.kind === 'group');
  const activeSignals = (signals || []).filter((s) => s.status === 'active');
  const dmContacts = contacts.filter((c) => !c.roomId);

  const renderMessages = () => {
    const out = [];
    let prevM = null;
    for (const m of activeList) {
      const day = dayLabel(m.createdAt);
      if (!prevM || dayLabel(prevM.createdAt) !== day) out.push(<div key={`d-${m.id}`} className="dsk-day">{day}</div>);
      const grouped =
        prevM && prevM.userId === m.userId && prevM.kind === 'text' && m.kind === 'text' && parseTs(m.createdAt) - parseTs(prevM.createdAt) < 3 * 60 * 1000;
      const mine = me && m.userId === me.id;
      if (m.kind === 'system') {
        out.push(<div key={m.id} className="dsk-system">{m.body} <small>{fmtClock(m.createdAt)}</small></div>);
      } else {
        out.push(
          <div key={m.id} className={`dsk-msg${grouped ? ' grouped' : ''}${mine ? ' mine' : ''}${m.deleted ? ' deleted' : ''}`}>
            {!grouped && (
              <div className="dsk-msg-head">
                <span className={`dsk-author${m.authorIsAdmin ? ' admin' : ''}`}>{m.author}{m.authorIsAdmin && <i title="Desk admin">admin</i>}</span>
                <span className="dsk-time">{fmtClock(m.createdAt)}</span>
              </div>
            )}
            {m.deleted ? (
              <div className="dsk-body muted">message deleted</div>
            ) : m.kind === 'signal' && m.meta?.signal ? (
              <SignalCard signal={m.meta.signal} isAdmin={isAdmin} onLoad={onLoadSignal} onClose={closeSignal} mid={mid} />
            ) : (
              <div className="dsk-body">{m.body}</div>
            )}
            {!m.deleted && (mine || isAdmin) && (
              <button type="button" className="dsk-del" title="Delete" onClick={() => del(m)}>×</button>
            )}
          </div>
        );
      }
      prevM = m;
    }
    return out;
  };

  return (
    <div className="dsk">
      <aside className="dsk-rail">
        <div className="dsk-rail-title">Rooms</div>
        {deskRoom && (
          <button type="button" className={`dsk-room${view === 'room' && activeId === deskRoom.id ? ' on' : ''}`} onClick={() => { setView('room'); setActiveId(deskRoom.id); }}>
            <span className="dsk-room-name"># Desk</span>
            {deskRoom.unread > 0 && <b className="dsk-unread">{deskRoom.unread}</b>}
            <small className="dsk-room-last">{deskRoom.last ? `${deskRoom.last.author}: ${deskRoom.last.preview}` : `${deskRoom.members} members`}</small>
          </button>
        )}
        <button type="button" className={`dsk-room${view === 'signals' ? ' on' : ''}`} onClick={() => setView('signals')}>
          <span className="dsk-room-name">⚡ Signals</span>
          {activeSignals.length > 0 && <b className="dsk-unread amber">{activeSignals.length}</b>}
          <small className="dsk-room-last">{activeSignals.length ? `${activeSignals.length} active` : 'Desk trade ideas'}</small>
        </button>

        <div className="dsk-rail-title">
          Direct messages
        </div>
        {dmRooms.map((r) => (
          <button type="button" key={r.id} className={`dsk-room${view === 'room' && activeId === r.id ? ' on' : ''}`} onClick={() => { setView('room'); setActiveId(r.id); }}>
            <span className="dsk-room-name">
              @ {r.name}
              {r.peer?.isAdmin && <i className="dsk-admin-dot" title="Desk admin" />}
            </span>
            {r.unread > 0 && <b className="dsk-unread">{r.unread}</b>}
            <small className="dsk-room-last">{r.last ? `${r.last.userId === me?.id ? 'you' : r.last.author}: ${r.last.preview}` : 'No messages yet'}</small>
          </button>
        ))}
        {dmContacts.length > 0 && (
          <select className="dsk-new-dm" value="" onChange={(e) => openDm(e.target.value)}>
            <option value="">{isAdmin ? '+ Message a trader / admin…' : '+ Message an admin…'}</option>
            {dmContacts.map((c) => (
              <option key={c.id} value={c.id}>{c.name}{c.isAdmin ? ' (admin)' : ''}</option>
            ))}
          </select>
        )}
        {!dmRooms.length && !dmContacts.length && <div className="dsk-muted dsk-pad">No one to message yet.</div>}

        <div className="dsk-rail-foot">
          <span className={`dsk-dot ${streamOk ? 'ok' : 'off'}`} />
          {streamOk ? 'live' : 'reconnecting…'}
          {me && <span className="dsk-me"> · you: {me.name}</span>}
        </div>
      </aside>

      <section className="dsk-main">
        {view === 'signals' ? (
          <>
            <div className="dsk-head">
              <strong>⚡ Signals</strong>
              <span className="dsk-muted">Admin trade ideas. Load one to prefill the ticket; you still choose size and confirm.</span>
              <div className="dsk-head-actions">
                <button type="button" className="dsk-btn" onClick={loadSignals}>Refresh</button>
                {isAdmin && <button type="button" className="dsk-btn primary" onClick={() => { setView('room'); setActiveId(deskRoom?.id ?? activeId); setShowSignalForm(true); }}>New signal</button>}
              </div>
            </div>
            <div className="dsk-list dsk-signals">
              {signals == null && <div className="dsk-muted dsk-pad">Loading…</div>}
              {signals && !signals.length && <div className="dsk-muted dsk-pad">No signals posted yet.</div>}
              {activeSignals.map((s) => <SignalCard key={s.id} signal={s} isAdmin={isAdmin} onLoad={onLoadSignal} onClose={closeSignal} mid={mid} />)}
              {signals && signals.some((s) => s.status !== 'active') && <div className="dsk-day">Closed</div>}
              {(signals || []).filter((s) => s.status !== 'active').map((s) => <SignalCard key={s.id} signal={s} compact isAdmin={isAdmin} onLoad={onLoadSignal} onClose={closeSignal} mid={mid} />)}
            </div>
          </>
        ) : (
          <>
            <div className="dsk-head">
              <strong>{activeRoom ? (activeRoom.kind === 'group' ? '# Desk' : `@ ${activeRoom.name}`) : 'Desk chat'}</strong>
              <span className="dsk-muted">
                {activeRoom?.kind === 'group'
                  ? `${activeRoom.members || 0} members · everyone with desk access`
                  : activeRoom
                  ? `Private · ${activeRoom.peer?.isAdmin ? 'desk admin' : 'trader'}`
                  : ''}
              </span>
              {isAdmin && activeRoom?.kind === 'group' && (
                <div className="dsk-head-actions">
                  <button type="button" className={`dsk-btn${showSignalForm ? '' : ' primary'}`} onClick={() => setShowSignalForm((s) => !s)}>
                    {showSignalForm ? 'Hide signal form' : '⚡ Post signal'}
                  </button>
                </div>
              )}
            </div>

            {showSignalForm && isAdmin && <SignalForm market={market} mid={mid} onSubmit={postSignal} onCancel={() => setShowSignalForm(false)} />}

            <div className="dsk-list" ref={listRef} onScroll={onScroll}>
              {msgs[activeId]?.hasMore && (
                <button type="button" className="dsk-older" onClick={loadOlder} disabled={loadingOlder}>
                  {loadingOlder ? 'Loading…' : 'Load earlier messages'}
                </button>
              )}
              {!msgs[activeId]?.loaded && <div className="dsk-muted dsk-pad">Loading…</div>}
              {msgs[activeId]?.loaded && !activeList.length && (
                <div className="dsk-muted dsk-pad">
                  {activeRoom?.kind === 'group' ? 'Quiet so far — say hello to the desk.' : 'Start the conversation.'}
                </div>
              )}
              {renderMessages()}
            </div>

            <div className="dsk-composer">
              <textarea
                rows={1}
                placeholder={activeRoom ? `Message ${activeRoom.kind === 'group' ? '#Desk' : activeRoom.name} — Enter to send, Shift+Enter for newline` : 'Select a room'}
                value={draft}
                maxLength={maxBody}
                disabled={activeId == null || sending}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
              />
              <button type="button" className="dsk-btn primary" disabled={!draft.trim() || sending || activeId == null} onClick={send}>
                Send
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
